import { useState, useMemo } from 'react';
import {
  Box,
  Group,
  Text,
  TextInput,
  Tabs,
  SimpleGrid,
  Badge,
  Tooltip,
  useMantineTheme,
  SegmentedControl,
} from '@mantine/core';
import { IconSearch, IconPlus, IconLayoutGrid, IconBoxMultiple, IconApps } from '@tabler/icons-react';
import { Modal, Button, Card } from '../../../ui';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { getAllWidgets, getWidgetsByCategory, searchWidgets } from '../registry';
import { getAllContainers } from '../containerRegistry';
import { WidgetDefinition, WidgetCategory, WIDGET_CATEGORIES, ContainerDefinition } from '../types';
import { FadeIn } from '../../../animations';
import {
  getWidgetSizeForBreakpoint,
  formatSizeDisplay,
  getSizeDescription,
  GRID_COLUMNS,
} from '../layout/breakpoints';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// WidgetPicker Component
// ============================================================================

export interface WidgetPickerProps {
  /** Whether the picker modal is open */
  opened: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export function WidgetPicker({ opened, onClose }: WidgetPickerProps) {
  const theme = useMantineTheme();
  const [pickerTab, setPickerTab] = useState<'widgets' | 'containers'>('widgets');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const addWidget = useDashboardStore((state) => state.addWidget);
  const addContainer = useDashboardStore((state) => state.addContainer);
  const widgets = useDashboardStore((state) => state.widgets);
  const containers = useDashboardStore((state) => state.containers);

  // Get filtered widgets
  const filteredWidgets = useMemo(() => {
    let result: WidgetDefinition[];

    if (searchQuery.trim()) {
      result = searchWidgets(searchQuery);
    } else if (activeCategory === 'all') {
      result = getAllWidgets();
    } else {
      result = getWidgetsByCategory(activeCategory as WidgetCategory);
    }

    return result;
  }, [searchQuery, activeCategory]);

  // Get categories with widget counts
  const categoriesWithCounts = useMemo(() => {
    const allWidgets = getAllWidgets();
    return WIDGET_CATEGORIES.map((cat) => ({
      ...cat,
      count: allWidgets.filter((w) => w.category === cat.key).length,
    })).filter((cat) => cat.count > 0);
  }, []);

  // Get all containers
  const allContainers = useMemo(() => getAllContainers(), []);

  // Filter containers by search
  const filteredContainers = useMemo(() => {
    if (!searchQuery.trim()) return allContainers;
    const query = searchQuery.toLowerCase();
    return allContainers.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
    );
  }, [allContainers, searchQuery]);

  // Check if widget is already added
  const isWidgetAdded = (widgetId: string) => {
    return widgets.some((w) => w.widgetId === widgetId);
  };

  // Handle adding widget
  const handleAddWidget = (widgetId: string) => {
    addWidget(widgetId);
    // Don't close modal to allow adding multiple widgets
  };

  // Handle adding container
  const handleAddContainer = (containerId: string) => {
    addContainer(containerId);
    // Don't close modal to allow adding multiple containers
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add to Dashboard"
      size="lg"
    >
      <Box>
        {/* Top-level Picker Type Toggle */}
        <SegmentedControl
          value={pickerTab}
          onChange={(value) => setPickerTab(value as 'widgets' | 'containers')}
          fullWidth
          mb="md"
          data={[
            {
              value: 'widgets',
              label: (
                <Group gap="xs" justify="center">
                  <IconApps size={16} />
                  <Text size="sm" style={accessibilityStyles}>Widgets</Text>
                </Group>
              ),
            },
            {
              value: 'containers',
              label: (
                <Group gap="xs" justify="center">
                  <IconBoxMultiple size={16} />
                  <Text size="sm" style={accessibilityStyles}>Containers</Text>
                </Group>
              ),
            },
          ]}
        />

        {/* Search */}
        <TextInput
          placeholder={pickerTab === 'widgets' ? 'Search widgets...' : 'Search containers...'}
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mb="md"
        />

        {/* Widgets Tab */}
        {pickerTab === 'widgets' && (
          <>
            {/* Category Tabs */}
            <Tabs
              value={activeCategory}
              onChange={(value) => setActiveCategory(value || 'all')}
              mb="md"
            >
              <Tabs.List>
                <Tabs.Tab value="all">
                  All ({getAllWidgets().length})
                </Tabs.Tab>
                {categoriesWithCounts.map((cat) => (
                  <Tabs.Tab key={cat.key} value={cat.key}>
                    {cat.label} ({cat.count})
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs>

            {/* Widget Grid */}
            {filteredWidgets.length === 0 ? (
              <Box
                style={{
                  textAlign: 'center',
                  padding: theme.spacing.xl,
                  color: theme.colors.gray[5],
                }}
              >
                <Text style={accessibilityStyles}>No widgets found</Text>
                {searchQuery && (
                  <Text size="sm" c="dimmed" mt="xs" style={accessibilityStyles}>
                    Try a different search term
                  </Text>
                )}
              </Box>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {filteredWidgets.map((widget, index) => (
                  <FadeIn key={widget.id} delay={index * 50}>
                    <WidgetCard
                      widget={widget}
                      isAdded={isWidgetAdded(widget.id)}
                      onAdd={() => handleAddWidget(widget.id)}
                    />
                  </FadeIn>
                ))}
              </SimpleGrid>
            )}
          </>
        )}

        {/* Containers Tab */}
        {pickerTab === 'containers' && (
          <>
            {/* Container description */}
            <Box
              mb="md"
              p="sm"
              style={{
                backgroundColor: theme.colors.blue[0],
                borderRadius: theme.radius.sm,
                border: `1px solid ${theme.colors.blue[2]}`,
              }}
            >
              <Text size="sm" c="blue.7" style={accessibilityStyles}>
                Containers let you group widgets together. They maintain their layout across different screen sizes.
              </Text>
            </Box>

            {/* Container Grid */}
            {filteredContainers.length === 0 ? (
              <Box
                style={{
                  textAlign: 'center',
                  padding: theme.spacing.xl,
                  color: theme.colors.gray[5],
                }}
              >
                <Text style={accessibilityStyles}>No containers found</Text>
                {searchQuery && (
                  <Text size="sm" c="dimmed" mt="xs" style={accessibilityStyles}>
                    Try a different search term
                  </Text>
                )}
              </Box>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {filteredContainers.map((container, index) => (
                  <FadeIn key={container.id} delay={index * 50}>
                    <ContainerCard
                      container={container}
                      onAdd={() => handleAddContainer(container.id)}
                    />
                  </FadeIn>
                ))}
              </SimpleGrid>
            )}
          </>
        )}

        {/* Footer */}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={onClose}>
            Done
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}

// ============================================================================
// WidgetCard Component
// ============================================================================

interface WidgetCardProps {
  widget: WidgetDefinition;
  isAdded: boolean;
  onAdd: () => void;
}

function WidgetCard({ widget, isAdded, onAdd }: WidgetCardProps) {
  const theme = useMantineTheme();
  const Icon = widget.icon;
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const categoryInfo = WIDGET_CATEGORIES.find((c) => c.key === widget.category);

  // Get default size for large breakpoint (most common desktop view)
  const defaultSize = getWidgetSizeForBreakpoint(widget.sizes, 'lg');
  const sizeText = formatSizeDisplay(defaultSize);
  const sizeDescription = getSizeDescription(defaultSize);

  // Check if widget has size variants
  const hasSizeVariants = widget.sizeVariants && widget.sizeVariants.variants.length > 1;

  return (
    <Card
      variant="outlined"
      p="sm"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={onAdd}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: theme.radius.md,
              backgroundColor: theme.colors.gray[1],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={20} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" lineClamp={1} style={accessibilityStyles}>
              {widget.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2} style={accessibilityStyles}>
              {widget.description}
            </Text>
            <Group gap="xs" mt={4}>
              {categoryInfo && (
                <Badge size="xs" variant="light">
                  {categoryInfo.label}
                </Badge>
              )}
              <Tooltip label={sizeDescription} withArrow>
                <Badge
                  size="xs"
                  variant="outline"
                  color="gray"
                  leftSection={<IconLayoutGrid size={10} />}
                >
                  {sizeText}
                </Badge>
              </Tooltip>
              {hasSizeVariants && (
                <Badge size="xs" variant="dot" color="green">
                  Resizable
                </Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Button
          size="xs"
          variant={isAdded ? 'light' : 'filled'}
          color={isAdded ? 'gray' : 'blue'}
          leftSection={<IconPlus size={14} />}
          disabled={false}
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
        >
          {isAdded ? 'Add Another' : 'Add'}
        </Button>
      </Group>

      {/* Visual size preview */}
      <Box mt="xs">
        <SizePreview cols={defaultSize.cols} rows={defaultSize.rows} />
      </Box>
    </Card>
  );
}

// ============================================================================
// SizePreview Component
// ============================================================================

interface SizePreviewProps {
  cols: number;
  rows: number;
}

function SizePreview({ cols, rows }: SizePreviewProps) {
  const theme = useMantineTheme();

  // Create a mini grid preview
  const previewWidth = 100; // Total width of preview
  const cellWidth = previewWidth / GRID_COLUMNS;
  const cellHeight = 6; // Fixed cell height for preview
  const gap = 1;

  const widgetWidth = cols * cellWidth - gap;
  const widgetHeight = Math.min(rows, 4) * cellHeight - gap; // Cap at 4 rows for preview

  return (
    <Box
      style={{
        width: previewWidth,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {/* Mini grid */}
      <Box
        style={{
          width: previewWidth,
          height: 24,
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
          gridTemplateRows: 'repeat(4, 1fr)',
          gap: gap,
          backgroundColor: theme.colors.gray[1],
          borderRadius: 2,
          padding: 2,
        }}
      >
        {/* Widget preview block */}
        <Box
          style={{
            gridColumn: `1 / ${Math.min(cols + 1, GRID_COLUMNS + 1)}`,
            gridRow: `1 / ${Math.min(rows + 1, 5)}`,
            backgroundColor: theme.colors.blue[4],
            borderRadius: 1,
          }}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// ContainerCard Component
// ============================================================================

interface ContainerCardProps {
  container: ContainerDefinition;
  onAdd: () => void;
}

function ContainerCard({ container, onAdd }: ContainerCardProps) {
  const theme = useMantineTheme();
  const Icon = container.icon;
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Get default size for large breakpoint
  const defaultSize = getWidgetSizeForBreakpoint(container.sizes, 'lg');
  const sizeText = formatSizeDisplay(defaultSize);
  const sizeDescription = getSizeDescription(defaultSize);

  // Check if container has size variants
  const hasSizeVariants = container.sizeVariants && container.sizeVariants.variants.length > 1;

  return (
    <Card
      variant="outlined"
      p="sm"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={onAdd}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: theme.radius.md,
              backgroundColor: theme.colors.violet[1],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={20} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" lineClamp={1} style={accessibilityStyles}>
              {container.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2} style={accessibilityStyles}>
              {container.description}
            </Text>
            <Group gap="xs" mt={4}>
              <Badge size="xs" variant="light" color="violet">
                {container.slots.length} slots
              </Badge>
              <Tooltip label={sizeDescription} withArrow>
                <Badge
                  size="xs"
                  variant="outline"
                  color="gray"
                  leftSection={<IconLayoutGrid size={10} />}
                >
                  {sizeText}
                </Badge>
              </Tooltip>
              {hasSizeVariants && (
                <Badge size="xs" variant="dot" color="green">
                  Resizable
                </Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Button
          size="xs"
          variant="filled"
          color="violet"
          leftSection={<IconPlus size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
        >
          Add
        </Button>
      </Group>

      {/* Visual slot preview */}
      <Box mt="xs">
        <ContainerSlotPreview container={container} />
      </Box>
    </Card>
  );
}

// ============================================================================
// ContainerSlotPreview Component
// ============================================================================

interface ContainerSlotPreviewProps {
  container: ContainerDefinition;
}

function ContainerSlotPreview({ container }: ContainerSlotPreviewProps) {
  const theme = useMantineTheme();

  // Create a mini grid preview based on internal grid
  const previewWidth = 100;
  const previewHeight = 24;
  const { cols: internalCols, rows: internalRows } = container.internalGrid;

  return (
    <Box
      style={{
        width: previewWidth,
        height: previewHeight,
        display: 'grid',
        gridTemplateColumns: `repeat(${internalCols}, 1fr)`,
        gridTemplateRows: `repeat(${internalRows}, 1fr)`,
        gap: 2,
        backgroundColor: theme.colors.gray[1],
        borderRadius: 2,
        padding: 2,
      }}
    >
      {container.slots.map((slot, index) => (
        <Box
          key={slot.id}
          style={{
            gridColumn: `${slot.col + 1} / ${slot.col + 1 + (slot.colSpan || 1)}`,
            gridRow: `${slot.row + 1} / ${slot.row + 1 + (slot.rowSpan || 1)}`,
            backgroundColor: theme.colors.violet[3 + (index % 3)],
            borderRadius: 1,
          }}
        />
      ))}
    </Box>
  );
}

export default WidgetPicker;
